import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LiveKitRoom,
  useTracks,
  VideoTrack,
  useLocalParticipant,
  RoomAudioRenderer,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';

const LIVEKIT_URL = 'wss://cherrypkd-cu73p5yu.livekit.cloud';

// ── Inner component — rendered inside LiveKitRoom ──────────────────
function SellerStudio({ session, listings, socket, onEnd }) {
  const { localParticipant } = useLocalParticipant();
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }]);
  const myVideoTrack = tracks.find(t => t.participant?.isLocal);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput]       = useState('');
  const [featured, setFeatured]         = useState(null);
  const [featuredIndex, setFeaturedIndex] = useState(-1);
  const [viewerCount, setViewerCount]   = useState(0);
  const [micMuted, setMicMuted]         = useState(false);
  const [camOff, setCamOff]             = useState(false);
  const [activeTab, setActiveTab]       = useState('items');
  const chatEndRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!socket) return;
    socket.emit('join_live', session.room_name);
    socket.on('live_chat', msg => setChatMessages(p => [...p.slice(-199), msg]));
    socket.on('live_viewer_count', count => {
      setViewerCount(count);
      API.patch(`/live/sessions/${session.room_name}`, { viewerCount: count }).catch(() => {});
    });
    return () => {
      socket.off('live_chat');
      socket.off('live_viewer_count');
      socket.emit('leave_live', session.room_name);
    };
  }, [socket, session.room_name]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const featureItem = useCallback((listing, idx) => {
    setFeatured(listing);
    setFeaturedIndex(idx ?? -1);
    API.patch(`/live/sessions/${session.room_name}`, { featuredListingId: listing?.id || null }).catch(() => {});
    socket?.emit('live_feature_item', { roomName: session.room_name, listing });
  }, [session.room_name, socket]);

  const nextItem = useCallback(() => {
    if (!listings.length) return;
    const next = featuredIndex < listings.length - 1 ? featuredIndex + 1 : 0;
    featureItem(listings[next], next);
  }, [listings, featuredIndex, featureItem]);

  const prevItem = useCallback(() => {
    if (!listings.length) return;
    const prev = featuredIndex > 0 ? featuredIndex - 1 : listings.length - 1;
    featureItem(listings[prev], prev);
  }, [listings, featuredIndex, featureItem]);

  const sendChat = useCallback(() => {
    if (!chatInput.trim()) return;
    socket?.emit('live_chat', {
      roomName: session.room_name,
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      message: chatInput.trim(),
      avatar: user.avatar_url,
    });
    setChatInput('');
  }, [chatInput, session.room_name, socket, user]);

  const toggleMic = () => {
    if (micMuted) localParticipant?.setMicrophoneEnabled(true);
    else localParticipant?.setMicrophoneEnabled(false);
    setMicMuted(p => !p);
  };

  const toggleCam = () => {
    if (camOff) localParticipant?.setCameraEnabled(true);
    else localParticipant?.setCameraEnabled(false);
    setCamOff(p => !p);
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', height:'100vh', background:'#0a0a0a', color:'white', overflow:'hidden' }}>
      {/* Left — Video + controls */}
      <div style={{ display:'flex', flexDirection:'column' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 20px', background:'rgba(255,255,255,.06)' }}>
          <div style={{ background:'#E53935', borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:800, letterSpacing:'1px' }}>● LIVE</div>
          <div style={{ fontWeight:700, fontSize:15 }}>{session.title}</div>
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8, fontSize:13, opacity:.7 }}>
            <span>👥 {viewerCount}</span>
          </div>
        </div>

        {/* Video */}
        <div style={{ flex:1, position:'relative', background:'#111' }}>
          {myVideoTrack && !camOff
            ? <VideoTrack trackRef={myVideoTrack} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            : <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', fontSize:64, opacity:.2 }}>📷</div>
          }
          {/* Featured item overlay */}
          {featured && (
            <div style={{ position:'absolute', bottom:20, left:20, background:'rgba(0,0,0,.85)', borderRadius:12, padding:'12px 16px', display:'flex', gap:12, alignItems:'center', maxWidth:320 }}>
              {featured.primary_image && (
                <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${featured.primary_image}`} alt="" style={{ width:56, height:56, borderRadius:8, objectFit:'cover', flexShrink:0 }} />
              )}
              <div>
                <div style={{ fontSize:11, opacity:.6, marginBottom:2 }}>NOW FEATURING</div>
                <div style={{ fontWeight:700, fontSize:14 }}>{featured.title}</div>
                <div style={{ color:'#4CAF50', fontWeight:800, fontSize:18 }}>${parseFloat(featured.price||0).toLocaleString()}</div>
              </div>
              <button onClick={() => featureItem(null)} style={{ position:'absolute', top:8, right:8, background:'none', border:'none', color:'rgba(255,255,255,.5)', cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
          )}
        </div>

        {/* Controls */}
        <div style={{ display:'flex', gap:10, padding:'12px 20px', background:'rgba(255,255,255,.06)', alignItems:'center', flexWrap:'wrap' }}>
          <button onClick={toggleMic} title={micMuted ? 'Unmute' : 'Mute'} style={{ background: micMuted ? '#E53935':'rgba(255,255,255,.15)', border:'none', color:'white', borderRadius:10, width:44, height:44, fontSize:18, cursor:'pointer', flexShrink:0 }}>
            {micMuted ? '🔇' : '🎙️'}
          </button>
          <button onClick={toggleCam} title={camOff ? 'Camera on' : 'Camera off'} style={{ background: camOff ? '#E53935':'rgba(255,255,255,.15)', border:'none', color:'white', borderRadius:10, width:44, height:44, fontSize:18, cursor:'pointer', flexShrink:0 }}>
            {camOff ? '📷' : '📸'}
          </button>

          {/* Item rotation controls */}
          {listings.length > 1 && (
            <div style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(255,255,255,.08)', borderRadius:10, padding:'6px 10px' }}>
              <button onClick={prevItem} title="Previous item" style={{ background:'rgba(255,255,255,.1)', border:'none', color:'white', borderRadius:7, width:30, height:30, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
              <span style={{ fontSize:12, color:'rgba(255,255,255,.6)', minWidth:60, textAlign:'center' }}>
                {featuredIndex >= 0 ? `${featuredIndex + 1} / ${listings.length}` : `— / ${listings.length}`}
              </span>
              <button onClick={nextItem} title="Next item" style={{ background:'rgba(255,255,255,.1)', border:'none', color:'white', borderRadius:7, width:30, height:30, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
            </div>
          )}

          <div style={{ flex:1 }} />
          <button onClick={onEnd} style={{ background:'#E53935', border:'none', color:'white', borderRadius:10, padding:'10px 20px', fontWeight:700, fontSize:13, cursor:'pointer' }}>
            End Stream
          </button>
        </div>
      </div>

      {/* Right — Items + Chat */}
      <div style={{ display:'flex', flexDirection:'column', borderLeft:'1px solid rgba(255,255,255,.1)', background:'#111' }}>
        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,.1)' }}>
          {[['items','📦 Items'], ['chat','💬 Chat']].map(([t,l]) => (
            <button key={t} onClick={() => setActiveTab(t)}
              style={{ flex:1, padding:'12px', background: activeTab===t?'rgba(255,255,255,.08)':'transparent', border:'none', color: activeTab===t?'white':'rgba(255,255,255,.4)', fontWeight:activeTab===t?700:400, fontSize:13, cursor:'pointer', borderBottom: activeTab===t?'2px solid #E53935':'2px solid transparent' }}>
              {l}
            </button>
          ))}
        </div>

        {/* Items tab */}
        {activeTab === 'items' && (
          <div style={{ flex:1, overflowY:'auto', padding:12 }}>
            {!featured && listings.length > 0 && (
              <div style={{ background:'rgba(229,57,53,.15)', border:'1px solid rgba(229,57,53,.3)', borderRadius:8, padding:'8px 12px', marginBottom:10, fontSize:12, color:'#FF8A65', fontWeight:600 }}>
                👇 Tap an item below to feature it on your stream
              </div>
            )}
            {featured && (
              <div style={{ fontSize:12, color:'rgba(255,255,255,.4)', marginBottom:10 }}>
                Tap the featured item again to unfeature it
              </div>
            )}
            {listings.length === 0 ? (
              <div style={{ textAlign:'center', padding:40, opacity:.4, fontSize:13 }}>No active listings</div>
            ) : listings.map((l, idx) => {
              const typeBadge = {
                auction: { label:'🔨 Auction', color:'#FF8A65' },
                barter:  { label:'🔄 Barter',  color:'#CE93D8' },
                rent:    { label:'📅 Rent',    color:'#90CAF9' },
                free:    { label:'🆓 Free',    color:'#A5D6A7' },
              }[l.listing_type];
              const priceLabel = l.listing_type === 'auction'
                ? `Starting $${parseFloat(l.starting_bid||0).toLocaleString()}`
                : l.listing_type === 'rent'
                  ? `$${parseFloat(l.price||0).toLocaleString()}/day`
                  : l.listing_type === 'free' ? 'FREE'
                  : l.listing_type === 'barter' ? 'Barter'
                  : `$${parseFloat(l.price||0).toLocaleString()}`;
              const isLive = featured?.id === l.id;
              const isNext = !isLive && featuredIndex >= 0 && idx === (featuredIndex + 1) % listings.length;
              return (
                <div key={l.id} onClick={() => featureItem(isLive ? null : l, idx)}
                  style={{ display:'flex', gap:10, alignItems:'center', padding:'10px', borderRadius:10, marginBottom:8, background: isLive?'rgba(229,57,53,.2)':'rgba(255,255,255,.05)', border:`1px solid ${isLive?'#E53935':isNext?'rgba(255,200,0,.3)':'rgba(255,255,255,.08)'}`, cursor:'pointer', position:'relative' }}>
                  {/* Queue number */}
                  <div style={{ width:20, height:20, borderRadius:'50%', background: isLive?'#E53935':isNext?'rgba(255,200,0,.4)':'rgba(255,255,255,.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, flexShrink:0, color:'white' }}>
                    {idx + 1}
                  </div>
                  {l.primary_image && (
                    <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${l.primary_image}`} alt="" style={{ width:40, height:40, borderRadius:8, objectFit:'cover', flexShrink:0 }} />
                  )}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
                      {typeBadge && (
                        <span style={{ fontSize:10, fontWeight:700, color: typeBadge.color, background:'rgba(255,255,255,.08)', borderRadius:4, padding:'1px 5px' }}>{typeBadge.label}</span>
                      )}
                    </div>
                    <div style={{ fontSize:13, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{l.title}</div>
                    <div style={{ fontSize:12, color:'#4CAF50', fontWeight:700 }}>{priceLabel}</div>
                    {l.listing_type === 'auction' && l.auction_ends_at && (
                      <div style={{ fontSize:11, color:'#FF8A65', opacity:.8 }}>
                        Ends {new Date(l.auction_ends_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  {isLive && <span style={{ fontSize:11, color:'#E53935', fontWeight:700, flexShrink:0 }}>● LIVE</span>}
                  {isNext && !isLive && <span style={{ fontSize:10, color:'rgba(255,200,0,.8)', fontWeight:600, flexShrink:0 }}>NEXT</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Chat tab */}
        {activeTab === 'chat' && (
          <>
            <div style={{ flex:1, overflowY:'auto', padding:12, display:'flex', flexDirection:'column', gap:8 }}>
              {chatMessages.length === 0 && <div style={{ textAlign:'center', opacity:.3, fontSize:13, marginTop:40 }}>Chat will appear here</div>}
              {chatMessages.map((m, i) => (
                <div key={i} style={{ fontSize:13, lineHeight:1.4 }}>
                  <span style={{ fontWeight:700, color:'#FF8A65' }}>{m.userName}: </span>
                  <span style={{ opacity:.85 }}>{m.message}</span>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div style={{ display:'flex', gap:8, padding:12, borderTop:'1px solid rgba(255,255,255,.1)' }}>
              <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key==='Enter' && sendChat()}
                placeholder="Say something..." style={{ flex:1, background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', borderRadius:8, padding:'8px 10px', color:'white', fontSize:13, outline:'none' }} />
              <button onClick={sendChat} style={{ background:'#E53935', border:'none', color:'white', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontWeight:700 }}>→</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────
export default function LivePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const socket   = useSocket();

  const [phase, setPhase]       = useState('setup');  // setup | live | ended
  const [title, setTitle]       = useState('');
  const [desc, setDesc]         = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError]       = useState('');
  const [lkError, setLkError]   = useState('');
  const [session, setSession]   = useState(null);
  const [token, setToken]       = useState('');
  const [listings, setListings] = useState([]);

  useEffect(() => {
    if (user) API.get('/users/me/listings?status=active').then(r => setListings(r.data.listings || [])).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div style={{ maxWidth:480, margin:'80px auto', textAlign:'center' }}>
        <h2>Sign in to go live</h2>
        <button className="btn btn-cherry" onClick={() => navigate('/login')}>Sign In</button>
      </div>
    );
  }

  async function handleGoLive() {
    if (!title.trim()) { setError('Stream title is required'); return; }
    setCreating(true); setError('');
    try {
      const { data: sessionData } = await API.post('/live/sessions', { title, description: desc });
      const { data: tokenData }   = await API.post('/live/token', { roomName: sessionData.session.room_name, isHost: true });
      setSession(sessionData.session);
      setToken(tokenData.token);
      setPhase('live');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start stream');
    } finally { setCreating(false); }
  }

  async function handleEnd() {
    if (!window.confirm('End your live stream?')) return;
    try {
      await API.patch(`/live/sessions/${session.room_name}`, { status: 'ended' });
      socket?.emit('live_ended', session.room_name);
    } catch {}
    setPhase('ended');
  }

  if (phase === 'ended') {
    return (
      <div style={{ maxWidth:520, margin:'80px auto', textAlign:'center', padding:24 }}>
        <div style={{ fontSize:64, marginBottom:16 }}>🎬</div>
        <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, marginBottom:8 }}>Stream Ended</h2>
        <p style={{ color:'var(--text-soft)', marginBottom:24 }}>Thanks for going live on CherryPKD!</p>
        <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
          <button className="btn btn-cherry" onClick={() => setPhase('setup')}>Go Live Again</button>
          <button className="btn btn-ghost" onClick={() => navigate('/dashboard')}>Dashboard</button>
        </div>
      </div>
    );
  }

  if (phase === 'live' && token && session) {
    return (
      <>
        {lkError && (
          <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:999, background:'#E53935', color:'white', padding:'12px 20px', fontSize:14, fontWeight:600, display:'flex', alignItems:'center', gap:12 }}>
            <span>⚠️ {lkError}</span>
            <button onClick={() => setLkError('')} style={{ marginLeft:'auto', background:'rgba(255,255,255,.2)', border:'none', color:'white', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:12 }}>Dismiss</button>
          </div>
        )}
        <LiveKitRoom
          token={token}
          serverUrl={LIVEKIT_URL}
          connect={true}
          video={true}
          audio={true}
          onError={(err) => {
            console.error('LiveKit error:', err);
            if (err.name === 'NotAllowedError') {
              setLkError('Camera/microphone access denied. Please allow permissions in your browser and try again.');
            } else if (err.name === 'NotFoundError') {
              setLkError('No camera or microphone found. Please connect a device and try again.');
            } else {
              setLkError(err.message || 'Connection error. Check your internet and try again.');
            }
          }}
        >
          <RoomAudioRenderer />
          <SellerStudio session={session} listings={listings} socket={socket} onEnd={handleEnd} />
        </LiveKitRoom>
      </>
    );
  }

  // Setup screen
  return (
    <div style={{ maxWidth:560, margin:'60px auto', padding:'0 24px 80px' }}>
      <div style={{ textAlign:'center', marginBottom:36 }}>
        <div style={{ fontSize:56, marginBottom:12 }}>🔴</div>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:30, fontWeight:700, marginBottom:8 }}>Go Live</h1>
        <p style={{ fontSize:15, color:'var(--text-soft)' }}>Sell to your followers in real-time. Showcase items, answer questions, and close deals instantly.</p>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom:16 }}>{error}</div>}

      <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:28 }}>
        <div className="form-group">
          <label className="form-label">Stream Title <span className="required">*</span></label>
          <input className="form-input" placeholder="e.g. Saturday Vintage Drop 🎸" value={title} onChange={e => setTitle(e.target.value)} maxLength={200} autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-textarea" placeholder="Tell viewers what you'll be selling today..." value={desc} onChange={e => setDesc(e.target.value)} style={{ minHeight:80 }} />
        </div>
        {/* Listing preview */}
        <div style={{ marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <label className="form-label" style={{ margin:0 }}>
              📦 Items to Feature
            </label>
            <span style={{ fontSize:12, color:'var(--text-soft)' }}>
              {listings.length} active listing{listings.length !== 1 ? 's' : ''}
            </span>
          </div>
          {listings.length === 0 ? (
            <div style={{ background:'var(--cream)', borderRadius:10, padding:'16px', textAlign:'center', fontSize:13, color:'var(--text-soft)' }}>
              No active listings yet. <a href="/list" style={{ color:'var(--cherry)', fontWeight:600 }}>Add a listing</a> before going live.
            </div>
          ) : (
            <div style={{ background:'var(--cream)', borderRadius:10, padding:10, display:'flex', flexDirection:'column', gap:6, maxHeight:220, overflowY:'auto' }}>
              <div style={{ fontSize:12, color:'var(--text-soft)', padding:'2px 4px 6px' }}>
                You'll select which item to showcase <strong>after</strong> you go live — just click any item in the studio panel.
              </div>
              {listings.map(l => {
                const typeIcon = { auction:'🔨', barter:'🔄', rent:'📅', free:'🆓' }[l.listing_type] || '💰';
                const priceLabel = l.listing_type === 'auction'
                  ? `Bid from $${parseFloat(l.starting_bid || 0).toLocaleString()}`
                  : l.listing_type === 'free' ? 'FREE'
                  : l.listing_type === 'barter' ? 'Barter'
                  : `$${parseFloat(l.price || 0).toLocaleString()}`;
                return (
                  <div key={l.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', background:'white', borderRadius:8, border:'1px solid var(--border)' }}>
                    {l.primary_image
                      ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${l.primary_image}`} alt="" style={{ width:36, height:36, borderRadius:6, objectFit:'cover', flexShrink:0 }} />
                      : <div style={{ width:36, height:36, borderRadius:6, background:'var(--cherry-light)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{typeIcon}</div>
                    }
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{l.title}</div>
                      <div style={{ fontSize:12, color:'var(--cherry)', fontWeight:600 }}>{typeIcon} {priceLabel}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button className="btn btn-cherry btn-lg btn-full" onClick={handleGoLive} disabled={creating || !title.trim()}>
          {creating ? <><span className="spinner" />Starting stream...</> : '🔴 Go Live'}
        </button>
        <p style={{ fontSize:12, color:'var(--text-soft)', textAlign:'center', marginTop:12 }}>
          Your camera and microphone will be requested when you go live.
        </p>
      </div>
    </div>
  );
}
